/**
 * TrackerPanel - the tracker (plan map-display, H; built as designed 2026-10-21, the Map Three Ways `tracker.html`).
 *
 * What the party's instruments say of what it cannot see, read off the published Stealth frame
 * (engine/stealth/contacts). Two halves:
 * - `TrackerGutter`, the DECORATION behind the whole Stealth column: a glowing blip on the column's edge at each
 *   contact bearing (contacts on one bearing merged, 10 px x the square root of the count, capped at 26; no number,
 *   the user rejected "+n"), its ring pinging out across the column (to 34 times its size at 0.18, 4 s), each bearing
 *   on its own beat 1.3 s apart; and a slow ring from the top FOR EACH figure at Signs (at most five, over 9 s).
 *   `aria-hidden`: the panel's lines say the same in words. Held still (reduced motion), nothing pings.
 * - `TrackerPanel`, the words: one line per bearing, or what Signs there are.
 * Nothing names a figure: nothing here could.
 *
 * @param {object} props
 * @param {{ signs: boolean, signCount: number, bearings: {compass: string, count: number}[] }|undefined} props.contacts
 */
import { useLayoutEffect, useRef, useState } from 'react';
import story from '../../styles/Story.module.css';
import styles from '../../styles/Stealth.module.css';
import { COMPASS } from '../../engine/stealth/contacts';

/** The small scope's middle and radius, in its own units; the dots sit on the ring. */
const C = 60;
const RING = 44;

/** A compass point's angle, radians: clockwise from east, as the map's +z runs south (engine/stealth/contacts). */
const bearingRad = (compass) => (COMPASS.indexOf(compass) * 45 * Math.PI) / 180;

const dotRadius = (count) => Math.min(12, 5 * Math.sqrt(count));
const lineOf = ({ compass, count }) => `${contactsPhrase(count, 'unknown ')} · ${compass}`;

/** Where a bearing sits on the small scope's ring: clockwise from east, as the map's +z runs south (contacts). */
function onRing(compass) {
  const a = bearingRad(compass);
  return { x: C + RING * Math.cos(a), y: C + RING * Math.sin(a) };
}

function Dot({ compass, count, at, index }) {
  const { x: cx, y: cy } = at;
  const r = dotRadius(count);
  // Each bearing on its own beat, as the gutter's blips (1.3 s apart).
  const delay = { animationDelay: `${(index * 1.3).toFixed(1)}s` };
  return (
    <g data-testid={`tracker-dot-${compass}`} data-count={count}>
      <circle className={styles.trackerPing} cx={cx} cy={cy} r={r} style={delay} />
      <circle className={styles.trackerDot} cx={cx} cy={cy} r={r} style={delay} />
    </g>
  );
}

/** The compass ticks, north, east, south and west, inside the ring: the scope reads as a compass. */
const TICKS = [
  [C, C - RING - 6, C, C - RING + 4],
  [C + RING + 6, C, C + RING - 4, C],
  [C, C + RING + 6, C, C + RING - 4],
  [C - RING - 6, C, C - RING + 4, C],
];

function Ticks() {
  return (
    <g data-testid="tracker-scope-ticks">
      {TICKS.map(([x1, y1, x2, y2]) => (
        <line key={`${x1},${y1}`} className={styles.trackerTick} x1={x1} y1={y1} x2={x2} y2={y2} />
      ))}
    </g>
  );
}

/**
 * A Signs ring for each figure at Signs, pinging out from the middle, staggered over 9 s. Each a little smaller
 * than the last, so held still (reduced motion) they stand apart and can still be counted.
 */
function ScopeSigns({ count }) {
  if (!count) return null;
  return (
    <g data-testid="tracker-signs" data-count={count}>
      {Array.from({ length: count }, (_, i) => (
        <circle
          key={i}
          className={styles.trackerSigns}
          cx={C}
          cy={C}
          r={RING - 4 - i * 6}
          style={{ animationDelay: `${((9 * i) / count).toFixed(1)}s` }}
        />
      ))}
    </g>
  );
}

/**
 * The strip's small scope (the canvas's Strip artboard, 2026-10-21): a firm ring with its four compass ticks, the
 * Signs rings, and a dot per contact bearing on the ring, each with its own ping. Read off the frame as the
 * ladder's reader left it (`signCount` always a number).
 */
export function TrackerScope({ contacts, className }) {
  const bearings = contacts?.bearings ?? [];
  return (
    <svg className={className} viewBox="0 0 120 120" role="img" aria-label="Tracker">
      <circle className={styles.trackerScope} cx={C} cy={C} r={RING} />
      <Ticks />
      <ScopeSigns count={contacts?.signCount ?? 0} />
      {bearings.map((b, i) => (
        <Dot key={b.compass} {...b} at={onRing(b.compass)} index={i} />
      ))}
    </svg>
  );
}

/** "1 contact", "3 contacts": the tracker's count, in both its readouts. */
const contactsPhrase = (n, kind = '') => `${n} ${kind}contact${n === 1 ? '' : 's'}`;

/**
 * What the tracker knows, in one line (the strip's words): "Signs · 3 contacts · east, north", "Signs", or
 * "Nothing on the tracker". A contact published with no bearing (no party on the map) still counts.
 */
export function trackerWords(contacts) {
  const bearings = contacts?.bearings ?? [];
  const signs = (contacts?.signCount ?? 0) > 0;
  const n = bearings.reduce((sum, b) => sum + b.count, 0) || (contacts?.contacts?.length ?? 0);
  if (!n) return signs ? 'Signs' : 'Nothing on the tracker';
  const where = bearings.length ? bearings.map((b) => b.compass).join(', ') : 'no bearing';
  const found = `${contactsPhrase(n)} · ${where}`;
  return signs ? `Signs · ${found}` : found;
}

/** The column's height over its width before it is measured (and in jsdom): about two and a half. */
const COLUMN_ASPECT = 2.5;

/**
 * Where a bearing from the column's middle meets its edge, as percentages of its width and height: east on the
 * right, north on top, a diagonal on the nearer side. `aspect` is the frame's height over its width, measured.
 */
export function gutterSpot(compass, aspect = COLUMN_ASPECT) {
  const a = bearingRad(compass);
  const [cos, sin] = [Math.cos(a), Math.sin(a)];
  const [hx, hy] = [0.5, 0.5 * aspect];
  const t = Math.min(
    Math.abs(cos) > 1e-9 ? hx / Math.abs(cos) : Infinity,
    Math.abs(sin) > 1e-9 ? hy / Math.abs(sin) : Infinity,
  );
  const pct = (v) => Math.round(v * 1000) / 10;
  return { left: pct(0.5 + t * cos), top: pct((hy + t * sin) / aspect) };
}

/** A blip's size in px: it grows with how many it holds (the study's 10 px x the square root, capped at 26). */
export const blipSize = (count) => Math.min(26, Math.round(10 * Math.sqrt(count)));

/** One bearing's blip on the column's edge, and its ring pinging out across the column; each on its own beat. */
function Blip({ compass, count, index, aspect }) {
  const size = blipSize(count);
  const { left, top } = gutterSpot(compass, aspect);
  const beat = `${(index * 1.3).toFixed(1)}s`;
  const at = (d) => ({ left: `${left}%`, top: `${top}%`, width: d, height: d, margin: -d / 2, animationDelay: beat });
  return (
    <span data-testid={`tracker-dot-${compass}`} data-count={count}>
      <span className={styles.gutterRing} style={at(size * 2)} />
      <span className={styles.gutterBlip} style={at(size)} />
    </span>
  );
}

/** Signs: one slow ring from the top for each figure at Signs (five at most), spread along it, staggered over 9 s. */
function SignRings({ count }) {
  if (!count) return null;
  const left = (i) => (count === 1 ? 50 : 30 + (40 * i) / (count - 1));
  return (
    <span data-testid="tracker-signs" data-count={count}>
      {Array.from({ length: count }, (_, i) => (
        <span
          key={i}
          className={styles.gutterSigns}
          style={{ left: `${left(i)}%`, animationDelay: `${((9 * i) / count).toFixed(1)}s` }}
        />
      ))}
    </span>
  );
}

/** The frame's height over its width, kept current as the column resizes: a diagonal's spot is read on it. */
function useAspect() {
  const ref = useRef(null);
  const [aspect, setAspect] = useState(COLUMN_ASPECT);
  useLayoutEffect(() => {
    const el = ref.current;
    const Observer = globalThis.ResizeObserver;
    if (!el || !Observer) return undefined;
    const read = () => el.clientWidth > 0 && setAspect(el.clientHeight / el.clientWidth || COLUMN_ASPECT);
    read();
    const observer = new Observer(read);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  return [ref, aspect];
}

/** The tracker drawn behind the Stealth column (the study's): decoration only, the panel's lines are the words. */
export function TrackerGutter({ contacts }) {
  const bearings = contacts?.bearings ?? [];
  const [ref, aspect] = useAspect();
  return (
    <div ref={ref} className={styles.gutter} aria-hidden="true" data-testid="tracker-gutter">
      <SignRings count={contacts?.signCount ?? 0} />
      {bearings.map((b, i) => (
        <Blip key={b.compass} {...b} index={i} aspect={aspect} />
      ))}
    </div>
  );
}

/** Signs in words: how many rings, with no bearing (the published count, so five at most). */
const signsLine = (n) => `Signs of ${n === 1 ? 'one' : n} - no bearing yet`;

export default function TrackerPanel({ contacts }) {
  const bearings = contacts?.bearings ?? [];
  const signCount = contacts?.signCount ?? 0;
  const lines = [...(signCount ? [signsLine(signCount)] : []), ...bearings.map(lineOf)];
  return (
    <section className={story.section} aria-label="The tracker" data-testid="tracker-panel">
      <h3 className={story.caption}>The tracker</h3>
      {bearings.length ? (
        <ul className={styles.trackerList}>
          {lines.map((line) => (
            <li key={line}>{line}</li>
          ))}
        </ul>
      ) : (
        <p className={story.empty} data-testid="tracker-quiet">
          {signCount ? `${signsLine(signCount)}.` : 'Nothing on the tracker.'}
        </p>
      )}
    </section>
  );
}
